import { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Tag, Info, HelpCircle, Mail } from "lucide-react";
import Hero from "@/components/sections/Hero";
import Features from "@/components/sections/Features";
import Preview from "@/components/sections/Preview";
import DownloadBand from "@/components/sections/DownloadBand";
import AccountControls from "@/components/sections/AccountControls";
import { Reveal } from "@/components/Reveal";

const CARDS = [
  { to: "/pricing", icon: Tag, title: "Plans & Pricing", desc: "Compare every lifetime plan in depth.", testid: "explore-pricing" },
  { to: "/about", icon: Info, title: "About", desc: "Why we built Warranty_Vault pro.", testid: "explore-about" },
  { to: "/faq", icon: HelpCircle, title: "FAQ", desc: "Answers about storage, reminders & devices.", testid: "explore-faq" },
  { to: "/contact", icon: Mail, title: "Contact", desc: "Questions, account help or bug reports.", testid: "explore-contact" },
];

function ExploreBand() {
  return (
    <section className="relative py-20 sm:py-24" data-testid="explore-band">
      <div className="mx-auto max-w-7xl px-5 sm:px-8">
        <Reveal className="max-w-2xl">
          <p className="eyebrow">Explore</p>
          <h2 className="mt-3 font-display text-3xl font-bold tracking-tight sm:text-4xl">Dive deeper</h2>
          <p className="mt-4 text-muted-foreground">Everything about Warranty_Vault pro, on its own page.</p>
        </Reveal>
        <div className="mt-10 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map((c) => (
            <Link
              key={c.to}
              to={c.to}
              data-testid={c.testid}
              className="group flex flex-col rounded-2xl border border-border bg-card/40 p-6 transition-all duration-300 hover:-translate-y-1 hover:border-gold/40"
            >
              <span className="grid h-11 w-11 place-items-center rounded-xl border border-gold/25 bg-gold/10 text-gold">
                <c.icon className="h-5 w-5" />
              </span>
              <h3 className="mt-4 font-display text-lg font-semibold">{c.title}</h3>
              <p className="mt-1 flex-1 text-sm text-muted-foreground">{c.desc}</p>
              <span className="mt-4 inline-flex items-center gap-1 text-sm font-semibold text-gold">
                Open <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5" />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function Home() {
  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.length > 1 && !hash.includes("=") && /^#[\w-]+$/.test(hash)) {
      try {
        const el = document.querySelector(hash);
        if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth" }), 120);
      } catch {
        /* ignore */
      }
    }
  }, []);

  return (
    <main data-testid="home-page">
      <Hero />
      <Features />
      <Preview />
      <DownloadBand />
      <ExploreBand />
      <AccountControls />
    </main>
  );
}
